import React, { useCallback, useEffect, useMemo, useState } from "react";
import { mediaUrl } from "@/lib/media";
import { ChevronLeft, ChevronRight, Star, Info, Maximize2 } from "lucide-react";
import { EmptyState } from "@/portal/ui/Layout";
import { Segmented } from "@/portal/ui/Tabs";
import { Select } from "@/portal/ui/Field";
import { Badge } from "@/portal/ui/Badge";
import { Lightbox } from "@/portal/ui/Modal";
import { fmtMonth } from "@/portal/format";

const DEMO_PHOTOS = [
  { id: "demo-1", url: "/demo/gallery-1.jpg", caption: "Structură în execuție – vedere generală", category: "Șantier", description: "Structura din beton armat a corpului principal, cu macaraua turn în funcțiune." },
  { id: "demo-2", url: "/demo/gallery-2.jpg", caption: "Fațadă vitrată – detaliu exterior", category: "Arhitectură", description: "Sistemul de fațadă cortină în lumină de după-amiază." },
  { id: "demo-3", url: "/demo/gallery-3.jpg", caption: "Lobby – tavan din lamele de lemn", category: "Arhitectură", description: "Zona de recepție cu lumină naturală și finisaje calde." },
  { id: "demo-4", url: "/demo/gallery-4.jpg", caption: "Cameră tehnică – instalații MEP", category: "Detalii", description: "Distribuție hidraulică și pompe în camera tehnică." },
  { id: "demo-5", url: "/demo/gallery-5.jpg", caption: "Detaliu arhitectural – tavan și coloană", category: "Detalii", description: "Racordul dintre lamelele de lemn și coloana de beton aparent." },
  { id: "demo-6", url: "/demo/gallery-6.jpg", caption: "Vedere aeriană – amenajări exterioare", category: "Șantier", description: "Lucrările de amenajare peisagistică în desfășurare." },
].map((p) => ({ ...p, demo: true, thumbnail_url: p.url, month: "" }));

export default function GalleryTab({ photos }) {
  const demo = photos.length === 0;
  const source = demo ? DEMO_PHOTOS : photos;
  const [month, setMonth] = useState("all");
  const [cat, setCat] = useState("all");
  const [sel, setSel] = useState(0);
  const [full, setFull] = useState(false);

  const months = useMemo(() => Array.from(new Set(source.map((p) => p.month).filter(Boolean))).sort().reverse(), [source]);
  const cats = useMemo(() => Array.from(new Set(source.map((p) => p.category).filter(Boolean))), [source]);
  const list = source.filter((p) => (month === "all" || p.month === month) && (cat === "all" || p.category === cat));
  const cur = list[sel] ?? null;
  useEffect(() => { setSel(0); }, [month, cat]);

  const go = useCallback((d) => setSel((i) => (list.length ? (i + d + list.length) % list.length : 0)), [list.length]);
  useEffect(() => {
    if (!full) return;
    const onKey = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, [full, go]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="cp-h1 !text-[26px]">Galeria proiectului</h2>
            {demo && <Badge tone="warning" data-testid="gallery-demo-badge">Previzualizare demo</Badge>}
          </div>
          <p className="cp-body mt-1">{demo ? "Imagini demonstrative ale proiectului. Galeria curentă este în curs de completare." : `${photos.length} ${photos.length === 1 ? "fotografie publicată" : "fotografii publicate"} de echipa Brisk.`}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Segmented ariaLabel="Categorie" value={cat} onChange={setCat} options={[{ value: "all", label: "Toate" }, ...cats.map((c) => ({ value: c, label: c }))]} />
          {months.length > 0 && (
            <Select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Selectează perioada" className="!w-auto !h-9 min-w-[190px]" data-testid="photo-month">
              <option value="all">Selectează perioada</option>{months.map((m) => <option key={m} value={m}>{fmtMonth(m)}</option>)}
            </Select>
          )}
        </div>
      </div>

      {list.length === 0 ? <EmptyState compact title="Nicio fotografie pentru filtrele alese" /> : (
        <div className={`grid grid-cols-1 gap-5 items-start ${cur ? "xl:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]" : ""}`}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="gallery-grid">
            {list.map((ph, i) => (
              <button key={ph.id} type="button" onClick={() => setSel(i)} data-testid={`gallery-photo-${ph.id}`} aria-pressed={sel === i} aria-label={ph.caption || `Fotografie ${ph.category}`}
                className="group text-left rounded-[var(--cp-radius)] focus-visible:outline-offset-4">
                <span className={`block relative aspect-[4/3] overflow-hidden rounded-[var(--cp-radius)] bg-cp-bg-subtle ring-offset-2 transition-shadow ${sel === i ? "ring-2 ring-cp-accent" : ""}`}>
                  <img src={mediaUrl(ph.thumbnail_url || ph.url)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                  {ph.demo && <span className="absolute bottom-2 left-2 text-[9.5px] font-bold tracking-wider uppercase bg-cp-navy/85 text-white px-1.5 py-0.5 rounded">Demo</span>}
                  {ph.featured && <span className="absolute top-2 left-2 h-6 w-6 rounded-full bg-white/95 text-cp-amber grid place-items-center shadow-[var(--cp-shadow-sm)]"><Star className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true" /></span>}
                </span>
                <span className="block mt-2 text-[13px] font-medium text-cp-text truncate">{ph.caption || ph.category || "Fotografie"}</span>
              </button>
            ))}
          </div>

          {cur && (
            <aside className="cp-panel p-5 xl:sticky xl:top-[calc(var(--cp-topbar-h)+24px)]" data-testid="gallery-detail" aria-label="Detaliu fotografie">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0"><h3 className="cp-h2 !text-[20px] truncate">{cur.caption || cur.category || "Fotografie"}</h3><p className="cp-meta mt-0.5">{cur.demo ? "Imagine demonstrativă" : [cur.category, cur.month && fmtMonth(cur.month)].filter(Boolean).join(" · ")}</p></div>
                <button type="button" onClick={() => setFull(true)} aria-label="Mărește" className="h-8 w-8 grid place-items-center rounded text-cp-text-3 hover:text-cp-text hover:bg-cp-surface-active shrink-0" data-testid="gallery-expand"><Maximize2 className="h-4 w-4" /></button>
              </div>
              <div className="relative mt-4 aspect-[4/3] rounded-[var(--cp-radius)] overflow-hidden bg-cp-bg-subtle">
                <img src={mediaUrl(cur.url)} alt={cur.caption || cur.category || ""} className="h-full w-full object-cover" />
                {cur.demo && <span className="absolute bottom-3 left-3 text-[10px] font-bold tracking-wider uppercase bg-cp-navy/85 text-white px-2 py-0.5 rounded">Demo</span>}
                {list.length > 1 && (
                  <>
                    <button type="button" onClick={() => go(-1)} aria-label="Fotografia anterioară" className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 grid place-items-center rounded-full bg-white/90 text-cp-text shadow-[var(--cp-shadow-md)] hover:bg-white" data-testid="gallery-prev"><ChevronLeft className="h-5 w-5" /></button>
                    <button type="button" onClick={() => go(1)} aria-label="Fotografia următoare" className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 grid place-items-center rounded-full bg-white/90 text-cp-text shadow-[var(--cp-shadow-md)] hover:bg-white" data-testid="gallery-next"><ChevronRight className="h-5 w-5" /></button>
                  </>
                )}
              </div>
              <p className="cp-body-sm mt-3 text-center">{cur.description || cur.caption || ""}</p>
              <p className="cp-meta mt-1 text-center tabular-nums">{sel + 1} / {list.length}</p>
            </aside>
          )}
        </div>
      )}

      {demo && (
        <div className="cp-panel-soft px-4 py-3 flex items-center gap-3 text-[13px] text-cp-text-2" data-testid="gallery-demo-note">
          <Info className="h-4 w-4 text-cp-accent shrink-0" aria-hidden="true" />
          <span><span className="font-semibold text-cp-text">Starea actuală:</span> nicio fotografie publicată. Fotografiile echipei vor apărea aici.</span>
        </div>
      )}

      <Lightbox open={full && !!cur} onOpenChange={(v) => !v && setFull(false)} label={cur?.caption || "Fotografie"}>
        {cur && (
          <figure className="relative w-full h-full flex flex-col items-center justify-center">
            <img src={mediaUrl(cur.url)} alt={cur.caption || cur.category || ""} className="max-h-[78vh] max-w-full object-contain rounded-[var(--cp-radius)]" />
            <figcaption className="mt-4 text-center text-white/85 text-[14px]">
              {cur.caption && <div className="font-medium">{cur.caption}</div>}
              <div className="text-white/55 text-[12.5px]">{cur.category}{cur.month ? ` · ${fmtMonth(cur.month)}` : ""} · {sel + 1} / {list.length}</div>
            </figcaption>
            {list.length > 1 && (
              <>
                <button type="button" onClick={() => go(-1)} aria-label="Fotografia anterioară" className="absolute left-0 top-1/2 -translate-y-1/2 h-11 w-11 grid place-items-center rounded-full bg-black/40 text-white hover:bg-black/60"><ChevronLeft className="h-5 w-5" /></button>
                <button type="button" onClick={() => go(1)} aria-label="Fotografia următoare" className="absolute right-0 top-1/2 -translate-y-1/2 h-11 w-11 grid place-items-center rounded-full bg-black/40 text-white hover:bg-black/60"><ChevronRight className="h-5 w-5" /></button>
              </>
            )}
          </figure>
        )}
      </Lightbox>
    </div>
  );
}
